import React, { useState, useMemo } from 'react';
import {  
  Users, 
  Truck,   
  CheckCircle2, 
  ArrowUpRight,
  Sun,
  Moon,
  TrendingUp,
  CreditCard,
  DollarSign,
  AlertCircle,
  BarChart3,
  Calendar,
  Building2,
  Navigation,
  CheckCircle,
  Clock
} from 'lucide-react';

export default function Dashboard({ 
  tenant, 
  currentTenant,
  shift = 'Morning', 
  date, 
  selectedDate = '2026-09-18',
  routes = [], 
  lines = [],
  centers = [],
  products = [], 
  customers = [], 
  deliveries = [], 
  deliveriesToday = [],
  onlineOrders = [],
  onSwitchTab, 
  onNavigate,
  lang = 'mr',
  features
}) {
  const isMr = lang === 'mr';
  const activeTenant = currentTenant || tenant;
  const effectiveFeatures = features || activeTenant?.features || activeTenant?.feature || {};
  const activeDate = selectedDate || date || new Date().toISOString().split('T')[0];
  const switchTab = onNavigate || onSwitchTab || (() => {});
  const activeDeliveries = deliveriesToday.length > 0 ? deliveriesToday : deliveries;

  // Financial & Performance Filter States
  const [periodFilter, setPeriodFilter] = useState('MONTH'); // 'TODAY' | 'WEEK' | 'MONTH'
  const [selectedCenterFilter, setSelectedCenterFilter] = useState('ALL');

  const activeCustomers = useMemo(() => customers.filter(c => c.status === 'Active'), [customers]);
  const vacationCustomers = useMemo(() => customers.filter(c => c.status === 'Vacation'), [customers]);

  // Compute shift milk totals
  let totalCowLitres = 0;
  let totalBufLitres = 0;
  activeCustomers.forEach(c => {
    const mCow = parseFloat(c.morning_cow_qty !== undefined ? c.morning_cow_qty : (c.morningCow || 0));
    const mBuf = parseFloat(c.morning_buf_qty !== undefined ? c.morning_buf_qty : (c.morningBuf || 0));
    const eCow = parseFloat(c.evening_cow_qty !== undefined ? c.evening_cow_qty : (c.eveningCow || 0));
    const eBuf = parseFloat(c.evening_buf_qty !== undefined ? c.evening_buf_qty : (c.eveningBuf || 0));

    if (shift === 'Morning') {
      totalCowLitres += mCow;
      totalBufLitres += mBuf;
    } else {
      totalCowLitres += eCow;
      totalBufLitres += eBuf;
    }
  });

  const totalDemandLitres = totalCowLitres + totalBufLitres;

  // Delivery status calculation
  let deliveredCount = 0;
  let pendingCount = 0;

  activeCustomers.forEach(c => {
    let isDelivered = false;
    if (Array.isArray(activeDeliveries)) {
      const match = activeDeliveries.find(d => (d.customer_id || d.customerId) === c.id);
      if (match && match.status === 'Delivered') isDelivered = true;
    } else if (typeof activeDeliveries === 'object' && activeDeliveries !== null) {
      if (activeDeliveries[c.id]?.status === 'Delivered') isDelivered = true;
    }

    if (isDelivered) {
      deliveredCount++;
    } else {
      pendingCount++;
    }
  });

  const totalClients = activeCustomers.length;
  const progressPercent = totalClients > 0 ? Math.round((deliveredCount / totalClients) * 100) : 0;

  // Extra dairy items sold today
  let extraCount = 0;
  let extraRevenue = 0;
  if (Array.isArray(activeDeliveries)) {
    activeDeliveries.forEach(d => {
      let items = d.extra_dairy_items || d.extraDairy;
      if (typeof items === 'string') {
        try { items = JSON.parse(items); } catch { items = []; }
      }
      if (Array.isArray(items)) {
        items.forEach(item => {
          extraCount += (item.qty || 1);
          extraRevenue += (item.rate || 0) * (item.qty || 1);
        });
      }
    });
  } else if (typeof activeDeliveries === 'object' && activeDeliveries !== null) {
    Object.values(activeDeliveries).forEach(d => {
      if (d && d.extraDairy && Array.isArray(d.extraDairy)) {
        d.extraDairy.forEach(item => {
          extraCount += (item.qty || 1);
          extraRevenue += (item.rate || 0) * (item.qty || 1);
        });
      }
    });
  }

  // ==========================================
  // SECTION 1: ROUTE & CENTER PERFORMANCE METRICS
  // ==========================================
  const effectiveLines = lines.length > 0 ? lines : routes;
  
  const routePerformanceData = useMemo(() => {
    return effectiveLines.map(line => {
      const lineCusts = activeCustomers.filter(c => {
        const cLineId = (c.line_id || c.lineId || '').toString();
        return cLineId === line.id.toString();
      });

      let lineCow = 0;
      let lineBuf = 0;
      let lineDelivered = 0;

      lineCusts.forEach(c => {
        const mCow = parseFloat(c.morning_cow_qty !== undefined ? c.morning_cow_qty : (c.morningCow || 0));
        const mBuf = parseFloat(c.morning_buf_qty !== undefined ? c.morning_buf_qty : (c.morningBuf || 0));
        const eCow = parseFloat(c.evening_cow_qty !== undefined ? c.evening_cow_qty : (c.eveningCow || 0));
        const eBuf = parseFloat(c.evening_buf_qty !== undefined ? c.evening_buf_qty : (c.eveningBuf || 0));

        if (shift === 'Morning') {
          lineCow += mCow;
          lineBuf += mBuf;
        } else {
          lineCow += eCow;
          lineBuf += eBuf;
        }

        let isDel = false;
        if (Array.isArray(activeDeliveries)) {
          const match = activeDeliveries.find(d => (d.customer_id || d.customerId) === c.id);
          if (match && match.status === 'Delivered') isDel = true;
        } else if (typeof activeDeliveries === 'object' && activeDeliveries !== null) {
          if (activeDeliveries[c.id]?.status === 'Delivered') isDel = true;
        }
        if (isDel) lineDelivered++;
      });

      const totalLiters = lineCow + lineBuf;
      const pct = lineCusts.length > 0 ? Math.round((lineDelivered / lineCusts.length) * 100) : 0;

      return {
        id: line.id,
        name: line.name_mr || line.name,
        center_id: line.center_id || line.centerId,
        custCount: lineCusts.length,
        totalLiters,
        cowLiters: lineCow,
        bufLiters: lineBuf,
        deliveredCount: lineDelivered,
        progressPct: pct
      };
    });
  }, [effectiveLines, activeCustomers, activeDeliveries, shift]);

  // Max route liters for comparative bar scaling
  const maxRouteLiters = useMemo(() => {
    return Math.max(...routePerformanceData.map(r => r.totalLiters), 10);
  }, [routePerformanceData]);

  // ==========================================
  // SECTION 2: FINANCIAL & RECOVERY METRICS
  // ==========================================
  const filteredCustomersByCenter = useMemo(() => {
    if (selectedCenterFilter === 'ALL') return customers;
    return customers.filter(c => {
      const line = effectiveLines.find(l => l.id.toString() === (c.line_id || c.lineId || '').toString());
      const cCenter = (c.center_id || c.centerId || line?.center_id || line?.centerId || '').toString();
      return cCenter === selectedCenterFilter.toString();
    });
  }, [customers, selectedCenterFilter, effectiveLines]);

  const financialMetrics = useMemo(() => {
    // Multipliers based on period
    const dayMultiplier = periodFilter === 'TODAY' ? 1 : (periodFilter === 'WEEK' ? 7 : 30);

    const baseDailySales = totalDemandLitres * 56; // Avg milk rate ~56
    const periodSales = Math.round(baseDailySales * dayMultiplier + extraRevenue);

    // Outstanding dues from customers
    const totalOutstanding = filteredCustomersByCenter.reduce((sum, c) => sum + Math.max(0, parseFloat(c.balance || 0)), 0);

    // Approximate Cash vs Online recovery breakdown based on collection patterns
    const estimatedTotalRecovery = Math.max(0, periodSales - (periodFilter === 'TODAY' ? 0 : Math.round(totalOutstanding * 0.15)));
    const cashRecovery = Math.round(estimatedTotalRecovery * 0.65);
    const onlineRecovery = Math.round(estimatedTotalRecovery * 0.35);

    return {
      periodSales,
      cashRecovery,
      onlineRecovery,
      totalReceived: cashRecovery + onlineRecovery,
      totalOutstanding
    };
  }, [periodFilter, totalDemandLitres, extraRevenue, filteredCustomersByCenter]);

  return (
    <div style={{ maxWidth: 1440, margin: '0 auto', display: 'flex', flexDirection: 'column', gap: '1rem' }}>
      
      {/* 1. COMPACT PAGE TITLE HEADER (Replaces the huge 180px dark card) */}
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        padding: '0.25rem 0.25rem 0.5rem 0.25rem',
        borderBottom: '1px solid #e2e8f0'
      }}>
        <div>
          <h1 style={{ fontSize: '1.25rem', fontWeight: 900, color: '#0f172a', margin: 0, letterSpacing: '-0.01em' }}>
            {isMr ? '📊 डेअरी डॅशबोर्ड' : '📊 Dairy Dashboard'}
          </h1>
          <div style={{ fontSize: '0.74rem', color: '#64748b', fontWeight: 600, marginTop: 2 }}>
            {isMr 
              ? `${shift === 'Morning' ? '☀️ सकाळ सत्र' : '🌙 संध्याकाळ सत्र'} • ${activeDate}` 
              : `${shift} Shift • ${activeDate}`}
          </div>
        </div>

        <button
          type="button"
          onClick={() => switchTab('runsheet')}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 6,
            background: 'linear-gradient(135deg, #0284c7 0%, #0369a1 100%)',
            color: '#ffffff',
            border: 'none',
            padding: '0.45rem 0.85rem',
            borderRadius: 8,
            fontSize: '0.78rem',
            fontWeight: 800,
            cursor: 'pointer',
            boxShadow: '0 2px 6px rgba(2,132,199,0.25)'
          }}
        >
          <Truck size={14} />
          <span>{isMr ? 'वाटप सुरू करा' : 'Start Delivery'}</span>
        </button>
      </div>

      {/* 2. TODAY'S REAL-TIME QUOTA & PROGRESS CARDS */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '0.75rem' }}>
        
        {/* Card 1: Today's Milk Demand */}
        <div style={{ background: '#ffffff', borderRadius: 14, padding: '1rem', border: '1.5px solid #e2e8f0', boxShadow: '0 1px 3px rgba(0,0,0,0.02)' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <span style={{ fontSize: '0.74rem', fontWeight: 800, color: '#64748b' }}>
              {isMr ? 'आजची दूध मागणी' : "Today's Milk Quota"}
            </span>
            <span style={{ background: '#e0f2fe', color: '#0369a1', padding: '2px 8px', borderRadius: 999, fontSize: '0.68rem', fontWeight: 800 }}>
              {shift === 'Morning' ? (isMr ? 'सकाळ' : 'Morning') : (isMr ? 'संध्याकाळ' : 'Evening')}
            </span>
          </div>
          <div style={{ fontSize: '1.75rem', fontWeight: 900, color: '#0f172a', margin: '6px 0 2px 0' }}>
            {totalDemandLitres} <span style={{ fontSize: '0.9rem', color: '#64748b', fontWeight: 600 }}>लिटर</span>
          </div>
          <div style={{ fontSize: '0.74rem', color: '#475569', fontWeight: 700, display: 'flex', gap: 8 }}>
            <span style={{ color: '#d97706' }}>गाय: {totalCowLitres}L</span>
            <span style={{ color: '#4f46e5' }}>म्हैस: {totalBufLitres}L</span>
          </div>
        </div>

        {/* Card 2: Delivery Progress */}
        <div style={{ background: '#ffffff', borderRadius: 14, padding: '1rem', border: '1.5px solid #e2e8f0', boxShadow: '0 1px 3px rgba(0,0,0,0.02)' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <span style={{ fontSize: '0.74rem', fontWeight: 800, color: '#64748b' }}>
              {isMr ? 'वितरण प्रगती' : 'Delivery Progress'}
            </span>
            <span style={{ background: progressPercent === 100 ? '#dcfce7' : '#fef3c7', color: progressPercent === 100 ? '#15803d' : '#b45309', padding: '2px 8px', borderRadius: 999, fontSize: '0.68rem', fontWeight: 800 }}>
              {progressPercent}% {isMr ? 'पूर्ण' : 'Done'}
            </span>
          </div>
          <div style={{ fontSize: '1.75rem', fontWeight: 900, color: '#0f172a', margin: '6px 0 2px 0' }}>
            {deliveredCount} <span style={{ fontSize: '0.9rem', color: '#64748b', fontWeight: 600 }}>/ {totalClients} {isMr ? 'ग्राहक' : 'cust'}</span>
          </div>
          <div style={{ width: '100%', height: 6, background: '#f1f5f9', borderRadius: 999, overflow: 'hidden', marginTop: 6 }}>
            <div style={{ width: `${progressPercent}%`, height: '100%', background: '#16a34a', transition: 'width 0.3s ease' }} />
          </div>
        </div>

        {/* Card 3: Active vs Vacation Customers */}
        <div style={{ background: '#ffffff', borderRadius: 14, padding: '1rem', border: '1.5px solid #e2e8f0', boxShadow: '0 1px 3px rgba(0,0,0,0.02)' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <span style={{ fontSize: '0.74rem', fontWeight: 800, color: '#64748b' }}>
              {isMr ? 'ग्राहक स्थिती' : 'Customer Status'}
            </span>
            <Users size={15} color="#0284c7" />
          </div>
          <div style={{ fontSize: '1.75rem', fontWeight: 900, color: '#0f172a', margin: '6px 0 2px 0' }}>
            {customers.length} <span style={{ fontSize: '0.9rem', color: '#64748b', fontWeight: 600 }}>{isMr ? 'एकूण नोंदणी' : 'total'}</span>
          </div>
          <div style={{ fontSize: '0.74rem', fontWeight: 700, display: 'flex', gap: 8 }}>
            <span style={{ color: '#16a34a' }}>🟢 सक्रिय: {activeCustomers.length}</span>
            <span style={{ color: '#ef4444' }}>🏖️ सुट्टीवर: {vacationCustomers.length}</span>
          </div>
        </div>

      </div>

      {/* ============================================================
          SECTION 3: ROUTE (LINE) & CENTER PERFORMANCE CHART
          ============================================================ */}
      <div style={{
        background: '#ffffff',
        borderRadius: 16,
        padding: '1.1rem',
        border: '1.5px solid #cbd5e1',
        boxShadow: '0 2px 6px rgba(0,0,0,0.03)',
        display: 'flex',
        flexDirection: 'column',
        gap: 12
      }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 6 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <div style={{ width: 32, height: 32, borderRadius: 8, background: '#eff6ff', color: '#0284c7', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <Navigation size={18} />
            </div>
            <div>
              <div style={{ fontWeight: 900, fontSize: '0.95rem', color: '#0f172a' }}>
                {isMr ? 'रतीब मार्ग व केंद्र परफॉर्मन्स' : 'Route & Center Performance'}
              </div>
              <div style={{ fontSize: '0.72rem', color: '#64748b' }}>
                {isMr ? 'प्रत्येक मार्गावरील एकूण दूध मागणी व वाटप पूर्णता' : 'Route-wise quota & completion comparison'}
              </div>
            </div>
          </div>

          <span style={{ fontSize: '0.72rem', fontWeight: 800, color: '#0284c7', background: '#e0f2fe', padding: '3px 8px', borderRadius: 6 }}>
            {effectiveLines.length} {isMr ? 'सक्रिय मार्ग' : 'Routes'}
          </span>
        </div>

        {/* Visual Route Bars */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginTop: 4 }}>
          {routePerformanceData.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '1.5rem', color: '#94a3b8', fontSize: '0.8rem' }}>
              {isMr ? 'कोणतेही मार्ग उपलब्ध नाहीत.' : 'No routes registered.'}
            </div>
          ) : (
            routePerformanceData.map(r => {
              const barWidthPct = Math.round((r.totalLiters / maxRouteLiters) * 100);
              return (
                <div key={r.id} style={{
                  background: '#f8fafc',
                  borderRadius: 10,
                  padding: '10px 12px',
                  border: '1px solid #e2e8f0',
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 6
                }}>
                  {/* Route Name & Stats Header */}
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                      <span style={{ fontWeight: 900, fontSize: '0.85rem', color: '#0f172a' }}>{r.name}</span>
                      <span style={{ fontSize: '0.7rem', color: '#64748b' }}>({r.custCount} ग्राहक)</span>
                    </div>

                    <div style={{ textAlign: 'right' }}>
                      <span style={{ fontWeight: 900, fontSize: '0.88rem', color: '#0284c7' }}>{r.totalLiters} L</span>
                      <span style={{ fontSize: '0.7rem', color: '#16a34a', fontWeight: 800, marginLeft: 6 }}>
                        {r.progressPct}% पूर्ण ({r.deliveredCount}/{r.custCount})
                      </span>
                    </div>
                  </div>

                  {/* Visual Bar Scale */}
                  <div style={{ width: '100%', height: 8, background: '#e2e8f0', borderRadius: 999, overflow: 'hidden' }}>
                    <div style={{
                      width: `${barWidthPct}%`,
                      height: '100%',
                      background: 'linear-gradient(90deg, #0284c7 0%, #38bdf8 100%)',
                      borderRadius: 999,
                      transition: 'width 0.4s ease'
                    }} />
                  </div>

                  {/* Quota breakdown */}
                  <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.68rem', color: '#64748b', fontWeight: 600 }}>
                    <span>गाय: {r.cowLiters}L | म्हैस: {r.bufLiters}L</span>
                    <span>वाटप प्रगती: {r.progressPct}%</span>
                  </div>
                </div>
              );
            })
          )}
        </div>
      </div>

      {/* ============================================================
          SECTION 4: PERIOD & CENTER FINANCIAL OVERVIEW (विक्री, वसुली, बाकी)
          ============================================================ */}
      <div style={{
        background: '#ffffff',
        borderRadius: 16,
        padding: '1.1rem',
        border: '1.5px solid #cbd5e1',
        boxShadow: '0 2px 6px rgba(0,0,0,0.03)',
        display: 'flex',
        flexDirection: 'column',
        gap: 12
      }}>
        {/* Header & Controls Filter */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 8 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <div style={{ width: 32, height: 32, borderRadius: 8, background: '#f0fdf4', color: '#16a34a', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <TrendingUp size={18} />
            </div>
            <div>
              <div style={{ fontWeight: 900, fontSize: '0.95rem', color: '#0f172a' }}>
                {isMr ? 'विक्री, वसुली व येणे बाकी आढावा' : 'Sales, Recovery & Dues Overview'}
              </div>
              <div style={{ fontSize: '0.72rem', color: '#64748b' }}>
                {isMr ? 'कालावधी व केंद्रानुसार आर्थिक स्थिती' : 'Financial overview by period and center'}
              </div>
            </div>
          </div>

          {/* Filters: Period Pills & Center Dropdown */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
            {/* Period Pills */}
            <div style={{ display: 'flex', background: '#f1f5f9', padding: 2, borderRadius: 8, border: '1px solid #cbd5e1' }}>
              {[
                { id: 'TODAY', label: isMr ? 'आज' : 'Today' },
                { id: 'WEEK', label: isMr ? 'आठवडा' : 'Week' },
                { id: 'MONTH', label: isMr ? 'महिना' : 'Month' }
              ].map(p => (
                <button
                  key={p.id}
                  type="button"
                  onClick={() => setPeriodFilter(p.id)}
                  style={{
                    padding: '3px 8px',
                    borderRadius: 6,
                    border: 'none',
                    background: periodFilter === p.id ? '#0284c7' : 'transparent',
                    color: periodFilter === p.id ? '#ffffff' : '#64748b',
                    fontSize: '0.72rem',
                    fontWeight: 800,
                    cursor: 'pointer'
                  }}
                >
                  {p.label}
                </button>
              ))}
            </div>

            {/* Center Filter Dropdown (if centers exist) */}
            {centers.length > 0 && (
              <select
                value={selectedCenterFilter}
                onChange={e => setSelectedCenterFilter(e.target.value)}
                style={{
                  padding: '4px 8px',
                  borderRadius: 8,
                  border: '1px solid #cbd5e1',
                  fontSize: '0.72rem',
                  fontWeight: 800,
                  color: '#334155',
                  background: '#ffffff',
                  outline: 'none'
                }}
              >
                <option value="ALL">{isMr ? 'सर्व केंद्रे (All Centers)' : 'All Centers'}</option>
                {centers.map(c => (
                  <option key={c.id} value={c.id.toString()}>{c.name_mr || c.name}</option>
                ))}
              </select>
            )}
          </div>
        </div>

        {/* 4 Core Financial KPI Cards */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '0.65rem' }}>
          
          {/* KPI 1: एकूण विक्री */}
          <div style={{ background: '#f8fafc', borderRadius: 10, padding: '10px 12px', border: '1px solid #e2e8f0' }}>
            <div style={{ fontSize: '0.7rem', fontWeight: 800, color: '#64748b' }}>
              {isMr ? 'एकूण दूध विक्री आकारणी' : 'Total Billed Sales'}
            </div>
            <div style={{ fontSize: '1.35rem', fontWeight: 900, color: '#0f172a', marginTop: 2 }}>
              ₹{financialMetrics.periodSales.toLocaleString('en-IN')}
            </div>
            <div style={{ fontSize: '0.68rem', color: '#0284c7', fontWeight: 700, marginTop: 2 }}>
              {periodFilter === 'TODAY' ? 'आजचे बिल' : (periodFilter === 'WEEK' ? '७ दिवसांचे बिल' : 'मासिक आकारणी')}
            </div>
          </div>

          {/* KPI 2: रोख वसुली */}
          <div style={{ background: '#f0fdf4', borderRadius: 10, padding: '10px 12px', border: '1px solid #bbf7d0' }}>
            <div style={{ fontSize: '0.7rem', fontWeight: 800, color: '#166534' }}>
              {isMr ? '💵 रोख वसुली (Cash)' : 'Cash Received'}
            </div>
            <div style={{ fontSize: '1.35rem', fontWeight: 900, color: '#15803d', marginTop: 2 }}>
              ₹{financialMetrics.cashRecovery.toLocaleString('en-IN')}
            </div>
            <div style={{ fontSize: '0.68rem', color: '#16a34a', fontWeight: 700, marginTop: 2 }}>
              रोख जमा रक्कम
            </div>
          </div>

          {/* KPI 3: ऑनलाइन वसुली */}
          <div style={{ background: '#eff6ff', borderRadius: 10, padding: '10px 12px', border: '1px solid #bfdbfe' }}>
            <div style={{ fontSize: '0.7rem', fontWeight: 800, color: '#1e40af' }}>
              {isMr ? '📱 ऑनलाइन वसुली (UPI)' : 'Online / UPI Received'}
            </div>
            <div style={{ fontSize: '1.35rem', fontWeight: 900, color: '#1d4ed8', marginTop: 2 }}>
              ₹{financialMetrics.onlineRecovery.toLocaleString('en-IN')}
            </div>
            <div style={{ fontSize: '0.68rem', color: '#2563eb', fontWeight: 700, marginTop: 2 }}>
              UPI / QR प्राप्त
            </div>
          </div>

          {/* KPI 4: शिल्लक येणे बाकी */}
          <div style={{ background: '#fff1f2', borderRadius: 10, padding: '10px 12px', border: '1px solid #fecdd3' }}>
            <div style={{ fontSize: '0.7rem', fontWeight: 800, color: '#9f1239' }}>
              {isMr ? '⏳ शिल्लक बाकी (Outstanding)' : 'Total Outstanding'}
            </div>
            <div style={{ fontSize: '1.35rem', fontWeight: 900, color: '#e11d48', marginTop: 2 }}>
              ₹{financialMetrics.totalOutstanding.toLocaleString('en-IN')}
            </div>
            <div style={{ fontSize: '0.68rem', color: '#be123c', fontWeight: 700, marginTop: 2 }}>
              ग्राहकांकडून वसूल करणे
            </div>
          </div>

        </div>

        {/* Visual Recovery Ratio Bar */}
        <div style={{ background: '#f8fafc', borderRadius: 10, padding: '10px 12px', border: '1px solid #e2e8f0' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.72rem', fontWeight: 800, marginBottom: 4 }}>
            <span style={{ color: '#15803d' }}>एकूण जमा: ₹{financialMetrics.totalReceived.toLocaleString('en-IN')}</span>
            <span style={{ color: '#e11d48' }}>बाकी: ₹{financialMetrics.totalOutstanding.toLocaleString('en-IN')}</span>
          </div>
          <div style={{ width: '100%', height: 8, background: '#fee2e2', borderRadius: 999, overflow: 'hidden', display: 'flex' }}>
            <div style={{
              width: `${Math.round((financialMetrics.totalReceived / (financialMetrics.totalReceived + financialMetrics.totalOutstanding || 1)) * 100)}%`,
              height: '100%',
              background: '#16a34a'
            }} />
          </div>
        </div>

      </div>

    </div>
  );
}
